import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  DropdownItem,
  DropdownTrigger,
  Dropdown,
  DropdownMenu,
  Avatar,
  Button,
} from '@/components/ui';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/auth';
import { StandardIcon } from './common/icon';
import { useNavigation, type Tab } from '@/hooks/navigation';
import { useBreakpoint } from '@/hooks/screen';
import { PeopleQuickSearch } from './common/input/quicksearch';
import { getInitials } from '@/shared/lib/names';
import { useCurrentUser } from '@/store/users/hooks';
import { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { cn } from '@/lib/cn';
import ActAsDialog from '@/components/act-as-dialog';
import { clearActAsUserId, isBetaClient } from '@/lib/actAs';

const TAB_LINK_CLASS =
  'whitespace-nowrap px-2 py-1.5 text-sm font-medium text-slate-600 hover:text-slate-900';

/** Tab keys that always appear in the More menu, never in the main bar */
const ALWAYS_IN_MORE_KEYS = [
  'people',
  'users',
  'structure',
  'access',
  'teams',
  'locations',
  'ministries',
  'platform',
];

/** Reserve for logo + quick search so nav item count doesn't overlap the left section */
const NAVBAR_LEFT_RESERVE_PX = 180;

export interface AppBarProps {
  showNotifications?: boolean;
  showSettings?: boolean;
}

export default function AppBar({
  showNotifications = false,
  showSettings = false,
}: AppBarProps = {}) {
  const navigate = useNavigate();
  const auth = useAuth();
  const { firebaseUser, logout, actingAsUserId } = auth;
  const actAsEnabled =
    isBetaClient() &&
    Boolean(auth.authorized) &&
    Boolean(auth.actorMetadata?.data.admin);
  const [actAsDialogOpen, setActAsDialogOpen] = useState(false);
  const user = useCurrentUser();
  const { activeTab, visibleTabs } = useNavigation();
  const photoUrl = actingAsUserId
    ? undefined
    : firebaseUser?.photoURL || undefined;
  const initials = getInitials(user?.data.displayName || '');

  const tabKeyToPath: Record<string, string> = {
    dashboard: '/',
    people: '/people-old',
    people2: '/people',
    groups: '/groups',
    teams: '/teams',
    structure: '/structure',
    access: '/access',
    ministries: '/ministries',
    locations: '/locations',
    users: '/users',
    communications: '/communications',
    migration: '/migration',
    platform: '/platform',
  };

  const handleTabChange = (key: string) => {
    const targetPath = tabKeyToPath[key] || '/';
    navigate(targetPath);
  };

  if (!firebaseUser) {
    return null;
  }

  const signOut = () => {
    logout();
  };

  const exitActAs = () => {
    clearActAsUserId();
    window.location.reload();
  };

  const maintenanceBannerEnabled = false;

  return (
    <>
      {maintenanceBannerEnabled && (
        <Navbar className='bg-warning-500' height='3rem'>
          <div className='flex flex-row items-center'>
            <StandardIcon id='mdi:alert-circle-outline' className='mr-2' />
            The system will be unavailable due to Scheduled Maintenance on 5/20
            from 9pm PDT to 10pm PDT.
          </div>
        </Navbar>
      )}
      <Navbar maxWidth='max-w-full' height='4rem' className='px-6'>
        {/* Left: logo + quick search — flex-none so it never competes with center */}
        <NavbarContent justify='start' className='flex-none gap-4'>
          <NavbarBrand className='shrink-0'>
            <img
              src='/ministryhub-black.svg'
              alt='Ministry Hub logo'
              width={140}
              height={41}
              className='h-[41px] w-auto'
            />
          </NavbarBrand>
          <NavbarItem className='flex min-w-0 shrink items-center md:max-w-[200px]'>
            <PeopleQuickSearch
              className='w-full'
              inputClassName='text-sm'
              compactButtonClassName='rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200'
            />
          </NavbarItem>
        </NavbarContent>

        {/* Center: nav items — fixed-width slot (flex-1 min-w-0) so it always uses remaining space; content changes don't shift left/right */}
        <NavbarContent
          justify='center'
          className='min-w-0 flex-1 basis-0 overflow-hidden'
        >
          <AppBarNav
            visibleTabs={visibleTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </NavbarContent>

        {/* Right: bell, gear, avatar — flex-none so it never competes with center */}
        <NavbarContent justify='end' className='flex-none items-center gap-2'>
          {showNotifications && (
            <Button
              variant='light'
              isIconOnly
              aria-label='Notifications'
              className='rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200'
            >
              <StandardIcon id='mdi:bell' className='text-xl' />
            </Button>
          )}
          {showSettings && (
            <Button
              variant='light'
              isIconOnly
              aria-label='Settings'
              className='rounded-lg bg-slate-100 text-slate-600 hover:bg-slate-200'
            >
              <StandardIcon id='mdi:cog' className='text-xl' />
            </Button>
          )}
          <Dropdown placement='bottom-end'>
            <DropdownTrigger>
              {photoUrl ? (
                <Avatar
                  as='button'
                  className={cn(
                    'h-9 w-9 transition-transform',
                    actingAsUserId &&
                      'ring-2 ring-[#ff531d] ring-offset-2 ring-offset-white',
                  )}
                  color='primary'
                  name={user?.data.displayName || undefined}
                  size='sm'
                  src={photoUrl}
                  showFallback
                  getInitials={() => initials}
                />
              ) : (
                <Avatar
                  as='button'
                  className={cn(
                    'h-9 w-9 transition-transform',
                    actingAsUserId &&
                      'ring-2 ring-[#ff531d] ring-offset-2 ring-offset-white',
                  )}
                  color='primary'
                  name={user?.data.displayName || undefined}
                  size='sm'
                  showFallback
                  getInitials={() => initials}
                />
              )}
            </DropdownTrigger>
            <DropdownMenu
              className='w-[340px]'
              itemClasses={{
                base: 'gap-4',
              }}
            >
              {actAsEnabled && (
                <DropdownItem
                  key={actingAsUserId ? 'exit-act-as' : 'act-as'}
                  startContent={
                    <StandardIcon
                      id={
                        actingAsUserId
                          ? 'mdi:exit-to-app'
                          : 'mdi:account-switch'
                      }
                    />
                  }
                  onPress={
                    actingAsUserId ? exitActAs : () => setActAsDialogOpen(true)
                  }
                >
                  {actingAsUserId ? (
                    <span className='flex min-w-0 flex-col'>
                      <span>Exit act as</span>
                      <span className='truncate text-xs text-slate-500'>
                        Currently viewing as{' '}
                        {user?.data.displayName || 'selected user'}
                      </span>
                    </span>
                  ) : (
                    'Act as user'
                  )}
                </DropdownItem>
              )}
              <DropdownItem
                key='sign-out'
                startContent={<StandardIcon id='mdi:logout' />}
                onPress={signOut}
              >
                Sign Out
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </NavbarContent>
      </Navbar>
      <ActAsDialog isOpen={actAsDialogOpen} onOpenChange={setActAsDialogOpen} />
    </>
  );
}

function TabLabel({ tab }: { tab: Tab }) {
  if (tab.state === 'deprecated') {
    return (
      <span className='flex w-full flex-row items-center gap-2'>
        <span className='flex-1 text-left'>{tab.title}</span>
        <StandardIcon id='mdi:warning-outline' size={18} />
      </span>
    );
  }
  if (tab.state === 'experimental') {
    return (
      <span className='flex w-full flex-row items-center gap-2'>
        <span className='flex-1 text-left'>{tab.title}</span>
        <StandardIcon id='mdi:flask' size={18} />
      </span>
    );
  }
  return <>{tab.title}</>;
}

function AppBarNav({
  visibleTabs,
  activeTab,
  onTabChange,
}: {
  visibleTabs: Tab[];
  activeTab: string;
  onTabChange: (key: string) => void;
}) {
  const breakpoint = useBreakpoint();
  const isSmallScreen =
    breakpoint === 'xs' || breakpoint === 'sm' || breakpoint === 'md';

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const [visibleCount, setVisibleCount] = useState(visibleTabs.length);

  // Tabs that always go in the More menu (never in the main bar)
  const primaryCandidates = visibleTabs.filter(
    (t) => !ALWAYS_IN_MORE_KEYS.includes(t.key),
  );
  const moreOnlyTabs = visibleTabs.filter((t) =>
    ALWAYS_IN_MORE_KEYS.includes(t.key),
  );
  // Order more-only as People, Users, Structure, Access, Teams, Locations,
  // Ministries, Platform.
  const moreOnlyOrdered = ALWAYS_IN_MORE_KEYS.map((key) =>
    moreOnlyTabs.find((t) => t.key === key),
  ).filter(Boolean) as Tab[];

  // On small screens, single dropdown shows all tabs in this order
  const allTabsForDropdown = [...primaryCandidates, ...moreOnlyOrdered];
  // For the dropdown list, put the current selection first
  const allTabsOrderedWithActiveFirst = (() => {
    const active = allTabsForDropdown.find((t) => t.key === activeTab);
    if (!active) {
      return allTabsForDropdown;
    }
    return [active, ...allTabsForDropdown.filter((t) => t.key !== activeTab)];
  })();
  const currentTitle =
    visibleTabs.find((t) => t.key === activeTab)?.title ?? 'Sections';
  const activeTabData = visibleTabs.find((t) => t.key === activeTab);

  const updateVisibleCount = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    const moreBtn = moreButtonRef.current;
    if (!container || !measure || primaryCandidates.length === 0) {
      return;
    }

    const containerWidth = Math.max(
      0,
      container.clientWidth - NAVBAR_LEFT_RESERVE_PX,
    );
    // Reserve space for the More button (use its actual width when rendered)
    const moreWidth = moreBtn ? moreBtn.offsetWidth + 24 : 80;
    const items = measure.querySelectorAll('[data-nav-item]');
    let sum = 0;
    let count = 0;
    for (const item of items) {
      const w = (item as HTMLElement).offsetWidth;
      if (sum + w + (count > 0 ? 24 : 0) + moreWidth > containerWidth) {
        break;
      }
      sum += w + (count > 0 ? 24 : 0);
      count++;
    }
    if (count === 0 && primaryCandidates.length > 0) {
      count = 1;
    }
    setVisibleCount((prev) => (count !== prev ? count : prev));
  }, [primaryCandidates.length]);

  // useLayoutEffect runs before paint — no visible flash of all-tabs state
  useLayoutEffect(() => {
    updateVisibleCount();
    const ro = new ResizeObserver(updateVisibleCount);
    const container = containerRef.current;
    if (container) {
      ro.observe(container);
    }
    return () => ro.disconnect();
  }, [primaryCandidates, updateVisibleCount]);

  if (visibleTabs.length === 0) {
    return null;
  }

  // Small screen: single dropdown showing current view title, all tabs inside
  if (isSmallScreen) {
    return (
      <div className='flex min-w-0 flex-1 items-center justify-center'>
        <Dropdown>
          <DropdownTrigger>
            <button
              type='button'
              className={cn(
                TAB_LINK_CLASS,
                'flex shrink-0 items-center gap-0.5 border-b-2 border-transparent',
                allTabsForDropdown.some((t) => t.key === activeTab) &&
                  'border-primary-600 font-semibold text-slate-900',
              )}
              aria-label='Current section'
            >
              <span className='flex items-center gap-1.5'>
                {currentTitle}
                {activeTabData?.state === 'deprecated' && (
                  <StandardIcon id='mdi:warning-outline' size={18} />
                )}
                {activeTabData?.state === 'experimental' && (
                  <StandardIcon id='mdi:flask' size={18} />
                )}
              </span>
              <StandardIcon
                id='mdi:chevron-down'
                className='ml-0.5 text-base'
              />
            </button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label='Sections'
            selectionMode='single'
            selectedKeys={
              allTabsForDropdown.some((t) => t.key === activeTab)
                ? new Set([activeTab])
                : new Set()
            }
            onAction={(key) => onTabChange(String(key))}
          >
            {allTabsOrderedWithActiveFirst.map((t) => (
              <DropdownItem key={t.key}>
                <TabLabel tab={t} />
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
    );
  }

  const primaryTabs = primaryCandidates.slice(0, visibleCount);
  const overflowFromPrimary = primaryCandidates.slice(visibleCount);
  const overflowTabs = [...overflowFromPrimary, ...moreOnlyOrdered];

  // When the current tab is in the overflow, show its title in place of "More"
  const isActiveInOverflow = overflowTabs.some((t) => t.key === activeTab);
  const overflowTriggerLabel = isActiveInOverflow
    ? (visibleTabs.find((t) => t.key === activeTab)?.title ?? 'More')
    : 'More';

  const overflowTriggerContent =
    isActiveInOverflow && activeTabData ? (
      <span className='flex items-center gap-1.5'>
        {overflowTriggerLabel}
        {activeTabData.state === 'deprecated' && (
          <StandardIcon id='mdi:warning-outline' size={18} />
        )}
        {activeTabData.state === 'experimental' && (
          <StandardIcon id='mdi:flask' size={18} />
        )}
      </span>
    ) : (
      overflowTriggerLabel
    );

  return (
    <div
      ref={containerRef}
      className='flex min-w-0 flex-1 items-center justify-center gap-6 overflow-hidden'
    >
      {/*
       * Measurement div: zero width & height, in normal flow (no absolute positioning).
       * Children inside still have their natural offsetWidth accessible to JS.
       */}
      <div className='h-0 w-0 flex-none overflow-hidden' aria-hidden>
        <div ref={measureRef} className='flex gap-6'>
          {primaryCandidates.map((t) => (
            <span
              key={t.key}
              data-nav-item
              className={cn(
                TAB_LINK_CLASS,
                'flex items-center gap-1.5 border-b-2 border-transparent',
              )}
            >
              {t.title}
              {t.state === 'deprecated' && (
                <StandardIcon id='mdi:warning-outline' size={18} />
              )}
              {t.state === 'experimental' && (
                <StandardIcon id='mdi:flask' size={18} />
              )}
            </span>
          ))}
        </div>
      </div>

      {primaryTabs.map((t) => (
        <button
          key={t.key}
          type='button'
          onClick={() => onTabChange(t.key)}
          className={cn(
            TAB_LINK_CLASS,
            'flex shrink-0 items-center gap-1.5 border-b-2 transition-colors',
            activeTab === t.key
              ? 'border-primary-600 font-semibold text-slate-900'
              : 'border-transparent',
          )}
        >
          {t.title}
          {t.state === 'deprecated' && (
            <StandardIcon id='mdi:warning-outline' size={18} />
          )}
          {t.state === 'experimental' && (
            <StandardIcon id='mdi:flask' size={18} />
          )}
        </button>
      ))}

      {overflowTabs.length > 0 && (
        <Dropdown>
          <DropdownTrigger>
            <button
              ref={moreButtonRef}
              type='button'
              className={cn(
                TAB_LINK_CLASS,
                'flex shrink-0 items-center gap-0.5 border-b-2 transition-colors',
                isActiveInOverflow
                  ? 'border-primary-600 font-semibold text-slate-900'
                  : 'border-transparent',
              )}
              aria-label={
                isActiveInOverflow ? 'Current section' : 'More sections'
              }
            >
              {overflowTriggerContent}
              <StandardIcon
                id='mdi:chevron-down'
                className='ml-0.5 text-base'
              />
            </button>
          </DropdownTrigger>
          <DropdownMenu
            aria-label='More sections'
            selectionMode='single'
            selectedKeys={
              overflowTabs.some((t) => t.key === activeTab)
                ? new Set([activeTab])
                : new Set()
            }
            onAction={(key) => onTabChange(String(key))}
          >
            {overflowTabs.map((t) => (
              <DropdownItem key={t.key}>
                <TabLabel tab={t} />
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      )}
    </div>
  );
}
